// modules
import { useCallback, useMemo, useState } from "react";

// validations
import { hasMinTenChars, hasAtleastOneUpperCase, hasAtleastOneLowerCase, hasAtleastOneSpecialChar } from "containers/FirstTimeLogin/passwordUtils";

type WatchWordChecks = {
	isMinChars: boolean;
	isUpperCase: boolean;
	isLowerCase: boolean;
	isSpecialChar: boolean;
};

export type FormDataValues = { watchword: string; confirm_watchword: string; verification_code: string };
export type WatchWordValidationState = "valid" | "invalid" | "";

export type UpdateWatchwordTypes = {
	watchWord: string;
	watchWordChecks: {
		isMinChars: boolean;
		isUpperCase: boolean;
		isLowerCase: boolean;
		isSpecialChar: boolean;
	};
	confirmWatchword: string;
	isDetailsValid: boolean;
	isValidWatchWord: WatchWordValidationState;
	verificationCode: string;
	isVerificationCodeInvalid: WatchWordValidationState;
	handleVerificationCode: (value: string) => void;
	onWatchwordChange: (value: string) => void;
	onWatchwordBlur: () => void;
	setConfirmWatchword: (value: string) => void;
	updateVerificationCodeValidation: (val: boolean) => void;
	submitChanges?: (values: FormDataValues) => void;
	backToLogin?: () => void;
};

export function useUpdateWatchword() {
	const [watchWord, setWatchWord] = useState<string>("");
	const [verificationCode, setVerificationCode] = useState<string>("");
	const [isValidWatchWord, setIsWatchWordValid] = useState<WatchWordValidationState>("");
	const [confirmWatchword, setConfirmWatchword] = useState<string>("");
	const [isVerificationCodeInvalid, setVerificationCodeInvalid] = useState<WatchWordValidationState>("");
	const [watchWordChecks, setWatchWordChecks] = useState<WatchWordChecks>({
		isMinChars: false,
		isUpperCase: false,
		isLowerCase: false,
		isSpecialChar: false
	});

	const resetAll = useCallback(() => {
		setWatchWord("");
		setIsWatchWordValid("");
		setConfirmWatchword("");
		setWatchWordChecks({
			isMinChars: false,
			isUpperCase: false,
			isLowerCase: false,
			isSpecialChar: false
		});
	}, []);

	const validateWatchword = useCallback(
		(value: string) => {
			const checks = {
				isMinChars: false,
				isUpperCase: false,
				isLowerCase: false,
				isSpecialChar: false
			};

			if (hasMinTenChars(value)) {
				checks.isMinChars = true;
			}
			if (hasAtleastOneUpperCase(value)) {
				checks.isUpperCase = true;
			}
			if (hasAtleastOneLowerCase(value)) {
				checks.isLowerCase = true;
			}
			if (hasAtleastOneSpecialChar(value)) {
				checks.isSpecialChar = true;
			}

			const isPasswordInvalid = !!Object.values(checks)?.filter((_value) => !_value)?.length;

			return { checks, isPasswordInvalid };
		},
		[setWatchWordChecks, setWatchWord, watchWordChecks, watchWord]
	);

	const onWatchwordChange = useCallback(
		(value: string) => {
			const { checks } = validateWatchword(value);

			setWatchWord(value);
			setWatchWordChecks({ ...watchWordChecks, ...checks });
			setIsWatchWordValid("");
		},
		[setWatchWordChecks, setWatchWord, watchWordChecks, watchWord]
	);

	const onWatchwordBlur = useCallback(() => {
		if (watchWord) {
			const { isPasswordInvalid } = validateWatchword(watchWord);

			if (!isPasswordInvalid) {
				setIsWatchWordValid("valid");
			} else {
				setIsWatchWordValid("invalid");
			}
		}
	}, [validateWatchword, setIsWatchWordValid, isValidWatchWord, setWatchWordChecks, setWatchWord, watchWordChecks, watchWord]);

	const isDetailsValid = useMemo(() => {
		const { isPasswordInvalid } = validateWatchword(watchWord);
		if (watchWord === confirmWatchword && !isPasswordInvalid && verificationCode) {
			return true;
		}
		return false;
	}, [watchWord, verificationCode, confirmWatchword, validateWatchword]);

	const handleVerificationCode = useCallback(
		(val: string) => {
			setVerificationCode(val);
			setVerificationCodeInvalid("");
		},
		[isVerificationCodeInvalid, verificationCode]
	);

	const updateVerificationCodeValidation = useCallback(
		(val: boolean) => {
			setVerificationCodeInvalid(val ? "invalid" : "valid");
		},
		[isVerificationCodeInvalid]
	);

	return {
		watchWord,
		watchWordChecks,
		confirmWatchword,
		isValidWatchWord,
		isDetailsValid,
		verificationCode,
		isVerificationCodeInvalid,
		updateVerificationCodeValidation,
		handleVerificationCode,
		resetAll,
		validateWatchword,
		onWatchwordBlur,
		onWatchwordChange,
		setConfirmWatchword
	};
}
